import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../api';
import type { Comparison, RatedPlayer, Team } from '../types';
import { PlayerColumn } from './PlayerColumn';
import { ComparisonTable } from './ComparisonTable';
import { FixturePanel } from './FixturePanel';

/**
 * The comparison workspace: two team columns, the head-to-head table they feed,
 * and the whole-fixture prediction for the same two teams.
 */
export function ComparePanel({ leagueId }: { leagueId: number }) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [allPlayers, setAllPlayers] = useState<RatedPlayer[]>([]);
  const [leftTeamId, setLeftTeamId] = useState<number | null>(null);
  const [rightTeamId, setRightTeamId] = useState<number | null>(null);
  const [leftSelected, setLeftSelected] = useState<number[]>([]);
  const [rightSelected, setRightSelected] = useState<number[]>([]);
  const [comparison, setComparison] = useState<Comparison | null>(null);
  const [error, setError] = useState<string | null>(null);

  // League changed: reload and drop every selection.
  useEffect(() => {
    let cancelled = false;
    setLeftTeamId(null);
    setRightTeamId(null);
    setLeftSelected([]);
    setRightSelected([]);
    setComparison(null);

    Promise.all([api.teams(leagueId), api.players(leagueId)])
      .then(([t, p]) => {
        if (cancelled) return;
        setTeams(t);
        setAllPlayers(p);
        setError(null);
        // Default to the top two teams so the view is useful immediately.
        if (t[0]) setLeftTeamId(t[0].id);
        if (t[1]) setRightTeamId(t[1].id);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : String(err));
      });

    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  const leftPlayers = useMemo(
    () => allPlayers.filter((p) => p.teamId === leftTeamId),
    [allPlayers, leftTeamId],
  );
  const rightPlayers = useMemo(
    () => allPlayers.filter((p) => p.teamId === rightTeamId),
    [allPlayers, rightTeamId],
  );

  // Preselect the four strongest players of each side — the list arrives sorted
  // by weighted score.
  useEffect(() => setLeftSelected(leftPlayers.slice(0, 4).map((p) => p.playerId)), [leftPlayers]);
  useEffect(() => setRightSelected(rightPlayers.slice(0, 4).map((p) => p.playerId)), [rightPlayers]);

  useEffect(() => {
    if (leftSelected.length === 0 && rightSelected.length === 0) {
      setComparison(null);
      return;
    }
    let cancelled = false;
    api
      .compare(leagueId, leftSelected, rightSelected)
      .then((result) => {
        if (!cancelled) {
          setComparison(result);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [leagueId, leftSelected, rightSelected]);

  const toggle = (side: 'left' | 'right') => (playerId: number) => {
    const apply = (current: number[]): number[] =>
      current.includes(playerId) ? current.filter((id) => id !== playerId) : [...current, playerId];
    if (side === 'left') setLeftSelected(apply);
    else setRightSelected(apply);
  };

  const leftTeam = teams.find((t) => t.id === leftTeamId) ?? null;
  const rightTeam = teams.find((t) => t.id === rightTeamId) ?? null;

  return (
    <>
      {error && <div className="notice error">{error}</div>}

      <section className="card">
        <header>
          <h2>Select players</h2>
          <p className="hint">
            Tick players in the order they should be paired — slot 1 plays slot 1.
          </p>
        </header>

        <div className="columns">
          <PlayerColumn
            title="Team A"
            accent="var(--home)"
            teams={teams}
            teamId={leftTeamId}
            onTeamChange={setLeftTeamId}
            players={leftPlayers}
            selected={leftSelected}
            onToggle={toggle('left')}
            onClear={() => setLeftSelected([])}
          />
          <PlayerColumn
            title="Team B"
            accent="var(--away)"
            teams={teams}
            teamId={rightTeamId}
            onTeamChange={setRightTeamId}
            players={rightPlayers}
            selected={rightSelected}
            onToggle={toggle('right')}
            onClear={() => setRightSelected([])}
          />
        </div>
      </section>

      {comparison && <ComparisonTable comparison={comparison} />}

      <FixturePanel
        leagueId={leagueId}
        homeTeam={leftTeam}
        awayTeam={rightTeam}
        homePlayers={leftPlayers}
        awayPlayers={rightPlayers}
      />
    </>
  );
}
